import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../lib/store';
import { authApi } from '../lib/api';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import logoApp from '../assets/logo-app.svg';
import logoMarkLight from '../assets/logo-mark-light.svg';

export default function LoginPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAuth = useAuthStore((state) => state.setAuth);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Login sets httpOnly cookie automatically via Set-Cookie header
            await authApi.login(email, password);

            // Cookies are now set — fetch user profile
            const [userData, tenantData] = await Promise.all([authApi.getMe(), authApi.getTenant()]);

            setAuth({
                ...userData,
                tenant_name: tenantData.name,
            });

            toast.success(t('auth.welcome', { defaultValue: 'Welcome!' }));
            navigate('/');
        } catch (error: any) {
            const data = error.response?.data;
            let message: string;
            // Backend returns the structured format { error: { code, message } }.
            // Translate the common auth code; otherwise fall back to the server
            // message, the legacy `detail` field, or a generic error.
            if (data?.error?.code === 'auth.invalid_credentials') {
                message = t('auth.invalidCredentials', { defaultValue: 'Invalid email or password' });
            } else if (data?.error?.message) {
                message = data.error.message;
            } else if (typeof data?.detail === 'string') {
                message = data.detail;
            } else if (Array.isArray(data?.detail)) {
                message = data.detail.map((e: any) => e.msg).join('. ');
            } else if (!error.response) {
                message = t('errors.networkError', { defaultValue: 'Could not reach the server. Check your connection and try again.' });
            } else {
                message = t('errors.serverError');
            }
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-500 to-forest-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-10" />
                <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
                    <img src={logoMarkLight} alt="Hit the Land" className="w-24 h-24 mb-8" />
                    <h1 className="font-display text-4xl font-bold mb-4 text-center">Hit the Land</h1>
                    <p className="text-xl text-white/80 text-center max-w-md">
                        Plataforma Enterprise para Cumplimiento EUDR y Monitoreo de Deforestación
                    </p>

                    <div className="mt-12 grid grid-cols-2 gap-6 text-center">
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                            <div className="text-3xl font-bold">7+</div>
                            <div className="text-sm text-white/80">Commodities</div>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                            <div className="text-3xl font-bold">Sentinel-2</div>
                            <div className="text-sm text-white/80">Imagery</div>
                        </div>
                    </div>
                </div>

                {/* Decorative circles */}
                <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-white/5" />
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/5" />
            </div>

            {/* Right side - Login form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-900">
                <div className="w-full max-w-md">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
                        <img src={logoApp} alt="Hit the Land" className="w-12 h-12 rounded-xl" />
                        <h1 className="font-display text-2xl font-bold text-gray-900 dark:text-white">Hit the Land</h1>
                    </div>

                    <div className="card p-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('auth.login')}</h2>
                        <p className="text-gray-500 dark:text-gray-400 mb-8">Ingresa tus credenciales para continuar</p>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('auth.email')}
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input"
                                    placeholder="usuario@empresa.com"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('auth.password')}
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="input pr-12"
                                        placeholder="••••••••"
                                        required
                                        minLength={8}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-sm">
                                <label className="flex items-center gap-2">
                                    <input type="checkbox" className="rounded border-gray-300" />
                                    <span className="text-gray-600 dark:text-gray-400">Recordarme</span>
                                </label>
                                <a href="#" className="text-primary-600 hover:text-primary-700 font-medium">
                                    {t('auth.forgotPassword')}
                                </a>
                            </div>

                            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                                {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                                {loading ? t('common.loading') : t('auth.login')}
                            </button>
                        </form>
                    </div>

                    {/* Demo credentials */}
                    <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-100 dark:border-blue-800">
                        <p className="text-sm text-blue-800 dark:text-blue-300">
                            <strong>Demo:</strong> Registra una cuenta nueva para acceder a la plataforma.
                        </p>
                    </div>

                    {/* Legal links */}
                    <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
                        <Link to="/terms" className="hover:text-gray-600 dark:hover:text-gray-300">
                            {t('legal.terms')}
                        </Link>
                        <span className="mx-2">·</span>
                        <Link to="/privacy" className="hover:text-gray-600 dark:hover:text-gray-300">
                            {t('legal.privacy')}
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
